function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { FormControl } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import { useTheme } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import CancelIcon from '@material-ui/icons/Cancel';
import PropTypes from 'prop-types';
import { DefaultText } from '../content';
const NoOptionsMessage = props => /*#__PURE__*/React.createElement(DefaultText, _extends({
  color: "text-secondary MuiAutocomplete-noOptions"
}, props.innerProps), props.children);
const inputComponent = _ref => {
  let {
    inputRef,
    ...props
  } = _ref;
  return /*#__PURE__*/React.createElement("div", _extends({
    ref: inputRef
  }, props));
};
const Control = props => {
  const {
    children,
    innerProps,
    innerRef,
    selectProps: {
      TextFieldProps
    }
  } = props;
  return /*#__PURE__*/React.createElement(TextField, _extends({
    InputProps: {
      inputComponent,
      inputProps: {
        ref: innerRef,
        style: {
          display: 'flex',
          padding: 0,
          height: 'auto'
        },
        children,
        ...innerProps
      }
    }
  }, TextFieldProps));
};
const Option = props => {
  const theme = useTheme();
  return /*#__PURE__*/React.createElement(MenuItem, _extends({
    key: Date.now(),
    style: {
      backgroundColor: props.isDisabled && theme.palette.error.light
    },
    ref: props.innerRef,
    selected: props.isFocused,
    component: "div"
  }, props.innerProps), props.children);
};
const Placeholder = props => {
  const {
    t
  } = useTranslation();
  const {
    innerProps = {}
  } = props;
  return /*#__PURE__*/React.createElement("p", _extends({
    className: "MuiAutocomplete-placeholder MuiTypography-colorTextSecondary"
  }, innerProps), "".concat(t('BOOK_WIDGET.select'), "..."));
};
const SingleValue = props => /*#__PURE__*/React.createElement("p", props.innerProps, props.children);
const ValueContainer = props => {
  const renderStyle = props.selectProps.dropdownPortal;
  const params = renderStyle ? {
    style: {
      overflow: 'hidden',
      flexWrap: 'nowrap'
    }
  } : {};
  return /*#__PURE__*/React.createElement("div", _extends({
    className: "MuiAutocomplete-values"
  }, params), props.children);
};
const MultiValue = props => {
  const theme = useTheme();
  return /*#__PURE__*/React.createElement(Chip, {
    style: {
      backgroundColor: props.data.isDisabled && theme.palette.error.light
    },
    tabIndex: -1,
    label: props.children,
    onDelete: props.removeProps.onClick,
    size: "small",
    deleteIcon: /*#__PURE__*/React.createElement(CancelIcon, _extends({}, props.removeProps, {
      size: "small"
    }))
  });
};
const Menu = props => /*#__PURE__*/React.createElement(Paper, _extends({
  square: true
}, props.innerProps), props.children);
const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer
};
const Autocomplete = _ref2 => {
  let {
    id,
    name,
    label,
    onChange,
    placeholder,
    isMultiSelect,
    options,
    value,
    throwError = false,
    margin = 'normal',
    className,
    fullWidth = false,
    required = true,
    dropdownPortal = false,
    ...rest
  } = _ref2;
  const {
    t
  } = useTranslation();
  const [localValue, setLocalValue] = useState(value === null || value === void 0 ? void 0 : value.map(x => ({
    value: x.value || x.id,
    label: x.label || x.name
  })));
  const [errorMessage, setErrorMessage] = useState(null);
  const elementReference = useRef();
  useEffect(() => {
    if (!elementReference) {
      return;
    }
    const validationListener = event => {
      event.stopPropagation();
      setErrorMessage({
        message: event.target.errormsg,
        param: event.target.errormsgparam
      });
      setLocalValue(event.target.value);
    };
    const elements = id ? document.querySelectorAll(".".concat(id, " [name=\"").concat(elementReference.current.props.name, "\"]")) : document.getElementsByName(elementReference.current.props.name);
    if (!elements || !elements.length) {
      return;
    }
    if (elements.length > 1 && !isMultiSelect) {
      throw new Error('Autocomplete duplicate components error');
    }
    const element = elements[0];
    required && element.setAttribute('requiredstr', 'true');
    element.addEventListener('invalid-data', validationListener);
    return () => {
      element && element.removeEventListener('invalid-data', validationListener);
    };
  }, [elementReference, id, isMultiSelect, label, required]);
  const triggerChangeForHiddenField = elementName => {
    const element = id ? document.querySelector(".".concat(id, " [name=\"").concat(elementName, "\"]")) : document.querySelector("[name=\"".concat(elementName, "\"]"));
    const ev = new Event('input', {
      bubbles: true
    });
    ev.simulated = true;
    element.type = 'text';
    const tracker = element._valueTracker;
    if (tracker) {
      tracker.setValue(element.value + +new Date());
    }
    element.dispatchEvent(ev);
    element.type = 'hidden';
  };
  const handleChange = (value, actionMeta) => {
    setLocalValue(value);
    setErrorMessage(null);
    onChange && onChange(value, actionMeta);
    triggerChangeForHiddenField(actionMeta.name);
  };
  rest.inputId = "".concat(+new Date(), "_select");
  const customStyles = {
    menuPortal: base => ({
      ...base,
      zIndex: 99999,
      boxShadow: '0 0 6px #ccc',
      maxHeight: '200px',
      width: 'auto',
      // Ensure dropdown appears above all elements
      whiteSpace: 'nowrap',
      // Prevents text from wrapping
      overflow: 'hidden' // Avoids horizontal scroll
    })
  };
  const selectComponentOptions = {
    ...rest,
    dropdownPortal,
    ref: elementReference,
    className: "MuiAutocomplete-root ".concat(throwError && !localValue && 'error', " ").concat(className, " ").concat(id),
    inputId: 'react-select-single',
    TextFieldProps: {
      label: label,
      InputLabelProps: {
        htmlFor: rest.inputId,
        shrink: true
      }
    },
    name,
    placeholder,
    options: options === null || options === void 0 ? void 0 : options.map(x => ({
      value: x.value || x.id,
      label: x.label || x.name
    })),
    components,
    value: localValue,
    onChange: handleChange,
    isMulti: isMultiSelect,
    styles: dropdownPortal && customStyles,
    menuPortalTarget: dropdownPortal && document.body
  };
  if (options && isMultiSelect && typeof selectComponentOptions.value === 'string') {
    var _selectComponentOptio;
    const initialValues = ((_selectComponentOptio = selectComponentOptions.value.split(',')) === null || _selectComponentOptio === void 0 ? void 0 : _selectComponentOptio.filter(x => x)) || [];
    selectComponentOptions.value = options.filter(x => initialValues.includes(x.value));
  }
  if (!!errorMessage) {
    selectComponentOptions.TextFieldProps.helperText = t(errorMessage.message).replace('[nome]', label).replace('[errormsgparam]', errorMessage.param);
    selectComponentOptions.TextFieldProps.error = true;
  }
  return /*#__PURE__*/React.createElement(FormControl, {
    margin: margin,
    className: className,
    fullWidth: fullWidth
  }, /*#__PURE__*/React.createElement(Select, selectComponentOptions));
};
export default Autocomplete;
Autocomplete.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  value: PropTypes.any,
  defaultValue: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  options: PropTypes.array
};